import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { CMDataGrid, DataGridFilterBy } from "cm-data-grid";
import { ThunkDispatch } from "redux-thunk";
import { Link } from "react-router-dom";
import {
  IAppState,
  LoadingState,
  ResponseError,
  ErrorDisplay,
  LoadingDisplay,
  appPaths
} from "../../../Common";
import { endpoints } from "../../Constants/endpoints";
import { Workflow } from "../../../Workflows/Models/Workflow";
import {
  IGetTopicWorkflowsArguments,
  IUnloadTopicWorkflowsArguments,
  ISetTopicWorkflowsLoadingArguments,
  IUpdateTopicWorkflowListItemArguments,
  TopicWorkflowAction,
  getWorkflows,
  unloadWorkflows,
  setLoading,
  IWorkflowListRemoveListItemAction,
  IWorkflowListRemoveWorkflowFromListArguments,
  removeItemFromList
} from "../../Actions/TopicWorkflowActions";
import CreateWorkflowModal from "../../../Workflows/Components/CreateWorkflowModal/CreateWorkflowModal";
import { WorkflowActionButtons } from "../../../Workflows";

type Props = {};

type StateProps = {
  workflows: Workflow[];
  workflowLoadingState: LoadingState;
  workflowErrorMessage: string;
  workflowErrorCode?: number;
};

type DispatchProps = {
  getWorkflows: (args: IGetTopicWorkflowsArguments) => void;
  unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => void;
  setLoading: (args: ISetTopicWorkflowsLoadingArguments) => void;
  updateListItem: (args: IUpdateTopicWorkflowListItemArguments) => void;
  removeListItem: (args: IWorkflowListRemoveWorkflowFromListArguments) => void;
};

type State = {
  tableData: any[][];
};

const mapStateToProps = (state: IAppState): StateProps => ({
  workflows: state.topicWorkflow.workflows,
  workflowLoadingState: state.topicWorkflow.loadingState,
  workflowErrorMessage: state.topicWorkflow.errorMessage,
  workflowErrorCode: state.topicWorkflow.errorCode
});

const mapDispatchToProps = (dispatch: ThunkDispatch<IAppState, {}, TopicWorkflowAction>) => ({
  getWorkflows: (args: IGetTopicWorkflowsArguments) => dispatch(getWorkflows(args)),
  unloadWorkflows: (args: IUnloadTopicWorkflowsArguments) => dispatch(unloadWorkflows(args)),
  setLoading: (args: ISetTopicWorkflowsLoadingArguments) => dispatch(setLoading(args)),
  updateListItem: (args: IUpdateTopicWorkflowListItemArguments) => dispatch(setLoading(args)),
  removeListItem: (args: IWorkflowListRemoveWorkflowFromListArguments) =>
    dispatch(removeItemFromList(args))
});

class TopicWorkflowListContainer extends React.Component<
  Props & StateProps & DispatchProps,
  State
> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.state = {
      tableData: []
    };

    this.onWorkflowDelete = this.onWorkflowDelete.bind(this);
  }

  componentDidMount = () => this.loadIfNeeded(this.props);

  componentDidUpdate = (prevProps: Props & StateProps) => this.loadIfNeeded(prevProps);

  componentWillUnmount = () => this.props.unloadWorkflows({});

  onWorkflowDelete(code: string) {
    this.props.removeListItem({ workflowCode: code });
    this.forceUpdate();
  }

  loadIfNeeded(prevProps: Props & StateProps) {
    const { isLoaded, isLoading, errored } = this.props.workflowLoadingState;

    if (!isLoaded && !isLoading && !errored) {
      this.reload();
    }

    // transform the table data if it needs to be transformed.
    if (prevProps.workflows.length !== this.props.workflows.length) {
      this.setState(prevState => ({
        ...prevState,
        tableData: this.mapWorkflowsToTableData()
      }));
    } else {
      // check the update count of each feedback- if any are different, rebuild the table data
      for (let i = 0; i < prevProps.workflows.length && i < this.props.workflows.length; i++) {
        if (prevProps.workflows[i].updateCount !== this.props.workflows[i].updateCount) {
          // we need to reload
          this.setState(prevState => ({
            ...prevState,
            tableData: this.mapWorkflowsToTableData()
          }));
          break;
        }
      }
    }
  }

  mapWorkflowsToTableData() {
    return this.props.workflows.map(el => {
      return [
        {
          displayValue: el.name,
          value: el.name,
          key: el.updateCount
        },
        {
          displayValue: <WorkflowActionButtons workflow={el} onDelete={this.onWorkflowDelete} />,
          value: "",
          key: el.updateCount
        }
      ];
    });
  }

  // dispatch the actions to load the workflows
  reload() {
    this.props.setLoading({});
    this.props.getWorkflows({});
  }

  render() {
    const { isLoaded, isLoading, errored } = this.props.workflowLoadingState;

    return (
      <>
        {errored && (
          <ErrorDisplay
            {...this.props}
            errorDescription={this.props.workflowErrorMessage}
            errorResult="Could not retrieve Workflows from server"
          />
        )}

        {!errored && isLoading && <LoadingDisplay />}

        {!errored && !isLoading && isLoaded && (
          <div>
            <CreateWorkflowModal />
            <CMDataGrid
              exportXls={false}
              exportPdf={false}
              changeableView
              selectableRow={false}
              pageLength={50}
              paging
              resizeConfigs={{
                resizable: false,
                options: {
                  liveDrag: true,
                  disabledColumns: []
                },
                serialize: false
              }}
              columns={[
                {
                  name: "Name",
                  searchable: true,
                  sortable: true,
                  filterable: { by: DataGridFilterBy.StartsWith }
                },
                {
                  name: "Actions",
                  searchable: false,
                  sortable: false
                }
              ]}
              useTiles={false}
              data={this.state.tableData}
            />
          </div>
        )}
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TopicWorkflowListContainer);
